import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as InfoIcon } from '@elementary/common-ui/assets/24x24/info.svg';
import {
  Flex,
  FlexColumn,
  InternalLink,
  PageAlert,
  SearchField,
} from '@elementary/common-ui/components';
import useHasDbtProjects from '@elementary/common-ui/hooks/useHasDbtProjects';
import { WarehouseType } from '@elementary/common-ui/types';
import { AddTestForm, Resource, TestTemplateMetadata } from 'shared/types';
import { toWizardSelectableTest } from 'testManagement/utils/utils';
import { space } from '@elementary/common-ui/style/utils';
import { isRectFullyVisible } from '../utils';
import { TestCatalogFilters } from './QualityDimensionTabs';
import TestCard from './TestCard';
import { getCloudTestDisabledReason } from './utils';

const Content = styled(FlexColumn)`
  padding: ${space('xxxl')};
  gap: ${space('xxxl')};
  overflow: auto;
`;

const CardsWrapper = styled(Flex)`
  flex-wrap: wrap;
  align-content: flex-start;
  gap: ${space('xxxl')};
  flex-shrink: 0;
`;

export type CardTemplate = {
  key: string;
  testName: string;
  packageName: string;
  description: string;
  qualityDimension?: string;
  testType: AddTestForm['testType'];
  popularity?: number;
  comingSoon?: boolean;
};

type Props = {
  testCatalogFilters: TestCatalogFilters;
  selectedTemplateId?: string;
  onCardClick: (val: CardTemplate) => void;
  elementaryTests: TestTemplateMetadata[];
  testsTemplatesData?: TestTemplateMetadata[];
  shouldConnectCodeRepo?: boolean;
  selectedTableResourcesData: Resource[];
  selectedTargetsCount: number;
  warehouseType?: WarehouseType;
};

const TestCardsSection = ({
  testCatalogFilters,
  selectedTemplateId,
  onCardClick,
  elementaryTests,
  testsTemplatesData = [],
  shouldConnectCodeRepo,
  selectedTableResourcesData = [],
  selectedTargetsCount,
  warehouseType,
}: Props) => {
  const [search, setSearch] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);
  const selectedCardRef = useRef<HTMLDivElement>(null);
  const previousTemplateIdRef = useRef(selectedTemplateId);
  const selectionFromUserRef = useRef(false);
  const hasDbtProjects = useHasDbtProjects();
  const shouldShowConnectCodeRepoAlert =
    shouldConnectCodeRepo && hasDbtProjects;

  const isSearchMatch = useCallback(
    (template: CardTemplate) =>
      search
        ? template.testName?.toLowerCase().includes(search.toLowerCase()) ||
          template.description?.toLowerCase().includes(search.toLowerCase())
        : true,
    [search],
  );

  const isQuickFilterMatch = useCallback(
    (template: CardTemplate) => {
      switch (testCatalogFilters.quickFilter) {
        case 'all':
          return true;
        case 'popular':
          return template.popularity === 1;
        default:
          return true;
      }
    },
    [testCatalogFilters.quickFilter],
  );

  const isSourceMatch = useCallback(
    (template: CardTemplate) => {
      switch (testCatalogFilters.sourceFilter) {
        case 'elementary cloud':
          return template.packageName === 'ELEMENTARY_CLOUD';
        case 'dbt':
          return template.packageName !== 'ELEMENTARY_CLOUD';
        default:
          return true;
      }
    },
    [testCatalogFilters.sourceFilter],
  );

  const isCategoryMatch = useCallback(
    (template: CardTemplate) => {
      switch (testCatalogFilters.categoryFilter) {
        case undefined:
          return true;
        case 'other':
          return !template.qualityDimension;
        default:
          return (
            template.qualityDimension === testCatalogFilters.categoryFilter
          );
      }
    },
    [testCatalogFilters.categoryFilter],
  );

  const mapTestTemplateToCard = (
    testTemplate: TestTemplateMetadata,
    source: 'api' | 'elementary',
  ): CardTemplate => {
    const option = toWizardSelectableTest(testTemplate, source);

    return {
      key: testTemplate.test_name,
      testName: option.name,
      packageName: option.package,
      description: testTemplate.description,
      qualityDimension: option.qualityDimension,
      testType: option.testType as AddTestForm['testType'],
      popularity: testTemplate.popularity,
      comingSoon: testTemplate.coming_soon,
    };
  };

  const testCardsData: CardTemplate[] | undefined = useMemo(
    () =>
      testsTemplatesData
        .map((testTemplate) => mapTestTemplateToCard(testTemplate, 'api'))
        .concat(
          elementaryTests.map((testTemplate) =>
            mapTestTemplateToCard(testTemplate, 'elementary'),
          ),
        )
        .filter(
          (testTemplate) =>
            isSearchMatch(testTemplate) &&
            isQuickFilterMatch(testTemplate) &&
            isSourceMatch(testTemplate) &&
            isCategoryMatch(testTemplate),
        ),
    [
      elementaryTests,
      isCategoryMatch,
      isQuickFilterMatch,
      isSearchMatch,
      isSourceMatch,
      testsTemplatesData,
    ],
  );

  const isTemplateSelected = (template: CardTemplate) => {
    const templateId =
      template.testType === 'yamlTest' ? template.key : template.testType;
    return !!templateId && templateId === selectedTemplateId;
  };

  useEffect(() => {
    const previousTemplateId = previousTemplateIdRef.current;
    previousTemplateIdRef.current = selectedTemplateId;
    if (selectionFromUserRef.current) {
      selectionFromUserRef.current = false;
      return;
    }
    const card = selectedCardRef.current;
    const container = contentRef.current;
    if (
      !selectedTemplateId ||
      selectedTemplateId === previousTemplateId ||
      !card ||
      !container ||
      isRectFullyVisible(
        card.getBoundingClientRect(),
        container.getBoundingClientRect(),
      )
    ) {
      return;
    }
    card.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [selectedTemplateId]);

  return (
    <FlexColumn sx={{ flex: 1, minWidth: 0 }}>
      {shouldShowConnectCodeRepoAlert && (
        <PageAlert icon={<InfoIcon />}>
          Only cloud tests can be added.{' '}
          <InternalLink to="/settings/environments">
            Connect your code repository
          </InternalLink>{' '}
          to add all test types from the UI.
        </PageAlert>
      )}
      <Content ref={contentRef}>
        <SearchField
          sx={{ width: '248px' }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <CardsWrapper>
          {testCardsData?.map((testTemplate) => {
            const disabledReason = testTemplate.comingSoon
              ? 'Coming soon'
              : getCloudTestDisabledReason({
                  packageName: testTemplate.packageName,
                  testType: testTemplate.testType,
                  warehouseType,
                  selectedTableResourcesData,
                  selectedTargetsCount,
                });
            return (
              <TestCard
                key={testTemplate.key}
                testName={testTemplate.testName}
                packageName={testTemplate.packageName}
                description={testTemplate.description}
                selected={isTemplateSelected(testTemplate) && !disabledReason}
                nodeRef={
                  isTemplateSelected(testTemplate) ? selectedCardRef : undefined
                }
                onClick={() => {
                  if (disabledReason) {
                    return;
                  }
                  selectionFromUserRef.current = true;
                  onCardClick(testTemplate);
                }}
                disabledReason={disabledReason}
                comingSoon={testTemplate.comingSoon}
              />
            );
          })}
        </CardsWrapper>
      </Content>
    </FlexColumn>
  );
};

export default TestCardsSection;
